import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Export,
  Plus,
  PrimaryButton,
  Spinner,
  Tab,
  TabContent,
  TabList,
  TabsProvider,
  TimerIcon,
  ViewIcon,
} from '@weareopenr/component-library';
import { useAuthWrapper } from '@weareopenr/authkit';
import { useLocation, useNavigate } from 'react-router-dom';
import { EProductLibraryStatus } from 'src/api/getProductLibrarySyncStatus/getProductLibrarySyncStatus';
import { EVENT_STATUSES, EVENT_TYPES } from 'src/api/getEvents';
import { publishProducts } from 'src/api/publishProducts/publishProducts';
import { PublishProductsType } from 'src/api/publishProducts/types';
import { CreateGlobalItemWizard } from 'src/components/CreateGlobalItemWizard/CreateGlobalItemWizard';
import { CancelScheduledProductPublicationPrompt } from 'src/components/CancelScheduledProductPublicationPrompt/CancelScheduledProductPublicationPrompt';
import { OptionGroupsTab } from 'src/components/OptionGroupsTab';
import { PageHeadingWithSync, IHeadingOptionMenuItem } from 'src/components/PageHeadingWithSync/PageHeadingWithSync';
import { ProductPublishModal } from 'src/components/ProductPublishModal/ProductPublishModal';
import { ProductsTab } from 'src/components/ProductsTab';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { SyncReportModal } from 'src/components/SyncReportModal/SyncReportModal';
import { getSyncEndpoint } from 'src/helpers/getSyncEndpoint/getSyncEndpoint';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useEvents } from 'src/hooks/useEvents';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';
import {
  IProductLibraryStatus,
  useProductLibraryStatus,
} from 'src/hooks/useProductLibraryStatus/useProductLibraryStatus';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useRejectSync } from 'src/hooks/useRejectSync/useRejectSync';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { CreateOptionGroupModal } from 'src/components/CreateOptionGroupModal/CreateOptionGroupModal';
import { FEATURE_FLAGS, IntegrationType } from 'src/constants';
import { Routes } from 'src/routes';
import styles from './Items.module.scss';

export interface RangeOption {
  id: string;
  label: string;
  display_order: number;
}

export const Items = (): ReactElement => {
  const [isItemWizardOpen, setIsItemWizardOpen] = useState<boolean>(false);
  const [isOptionGroupModalOpen, setIsOptionGroupModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProductPublishOpen, setIsProductPublishOpen] = useState<boolean>(false);
  const [isCancelPublishPromptOpen, setIsCancelPublishPromptOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { trackEvent } = useUserTracking();
  const { refetch: refetchExternalSystems, data: externalSystems = [] } = useExternalSystems();
  const { rejectSync } = useRejectSync();
  const { featureFlags } = useAuthWrapper();
  const withIndependentOptionGroups = featureFlags.includes(FEATURE_FLAGS.INDEPENDENT_OPTION_GROUPS);

  const onSyncStatusUpdate = (data: IProductLibraryStatus) => {
    // only refetch products after status changes from PENDING -> SUCCESS sync
    // status call count avoids refetch on page render if a previous sync was successful
    if (data?.sync_status === EProductLibraryStatus.SUCCESS && data.callCount > 1) {
      queryClient.refetchQueries({ queryKey: [QueryKeys.productSizes] });
      refetchExternalSystems();
    }
  };
  const isProductSyncEnabled = useMemo(() => !!getSyncEndpoint(externalSystems), [externalSystems]);
  const { isSyncInProgress, triggerSync, syncInfo } = useProductLibraryStatus({
    onSyncStatusUpdate,
    enabled: isProductSyncEnabled,
  });

  const isPosSystemEnabled = externalSystems.some(
    ({ integrationType }) => integrationType === IntegrationType.POINT_OF_SALE_MANAGEMENT,
  );
  const { data: events } = useEvents({ event_type: EVENT_TYPES.PUBLISH }, { enabled: isPosSystemEnabled });

  const { isPublishScheduled, scheduledPublishEvent, latestPublishEvent } = useMemo(() => {
    if (!events?.data?.length) {
      return { isPublishScheduled: false, scheduledPublishEvent: null, latestPublishEvent: null };
    }

    const scheduledEvent = events.data.find((event) => event.status === EVENT_STATUSES.SCHEDULED) || null;
    const latestEvent = scheduledEvent || events.data[0] || null;

    return {
      isPublishScheduled: !featureFlags.includes(FEATURE_FLAGS.ENHANCED_SCHEDULING) && !!scheduledEvent,
      scheduledPublishEvent: scheduledEvent,
      latestPublishEvent: latestEvent,
    };
  }, [events, featureFlags]);

  const handlePublishClick = async () => {
    if (featureFlags.includes(FEATURE_FLAGS.PRODUCT_PUBLISH_FLOW)) {
      setIsProductPublishOpen(true);
    } else {
      try {
        setIsLoading(true);
        await publishProducts({ publish_type: [PublishProductsType.PRODUCT] });
      } catch (error) {
        console.error('Error publishing products:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancelScheduledPublish = () => {
    setIsCancelPublishPromptOpen((prev) => !prev);
  };

  const { data: ranges, isLoading: isRangesLoading } = useRanges();
  const rangeOptions: RangeOption[] = useMemo(
    () => ranges?.map((range, index) => ({ id: range.range_id, label: range.name, display_order: index })) || [],
    [ranges],
  );

  useEffect(() => {
    document.title = 'Items';
    trackEvent('page_viewed', { page: 'product_library' });
  }, [trackEvent]);

  const optionMenuItems: IHeadingOptionMenuItem[] = [
    {
      name: 'New product',
      onOptionClick: () => setIsItemWizardOpen(true),
      id: 'add-product',
      isDisabled: isSyncInProgress,
      icon: Plus,
    },
    ...(withIndependentOptionGroups
      ? [
          {
            name: 'New option group',
            onOptionClick: () => setIsOptionGroupModalOpen(true),
            id: 'add-option-group',
            isDisabled: isSyncInProgress,
            icon: Plus,
          },
        ]
      : []),
    ...(isPosSystemEnabled
      ? [
          {
            name: 'View Publish Status',
            onOptionClick: () =>
              latestPublishEvent && navigate(`${location.pathname}/status/${latestPublishEvent.event_id}`),
            id: 'view-publish-status',
            isDisabled: isSyncInProgress || !latestPublishEvent,
            icon: ViewIcon,
          },
        ]
      : []),
  ];

  const publishButton = isPosSystemEnabled ? (
    isPublishScheduled ? (
      <PrimaryButton onClick={handleCancelScheduledPublish} icon={TimerIcon}>
        Scheduled
      </PrimaryButton>
    ) : (
      <PrimaryButton onClick={handlePublishClick} disabled={isLoading || isSyncInProgress} icon={Export}>
        Publish
      </PrimaryButton>
    )
  ) : undefined;

  return (
    <TabsProvider defaultTab="products">
      <div className={styles['page-container']}>
        <PageHeadingWithSync
          optionMenuItems={optionMenuItems}
          entityLabel="Products"
          isSyncEnabled={true}
          isSyncInProgress={isSyncInProgress}
          triggerSync={triggerSync}
          rejectSync={rejectSync}
          syncStatus={syncInfo?.sync_status}
          reviewRoutePath={Routes.PRODUCT_LIBRARY}
          renderReviewModal={({ isOpen, closeModal }) => (
            <SyncReportModal
              isOpen={isOpen}
              closeModal={closeModal}
              products={syncInfo?.diff}
              rejectSync={rejectSync}
            />
          )}
          primaryAction={publishButton}
          extraModals={
            <>
              <ProductPublishModal isOpen={isProductPublishOpen} closeModal={() => setIsProductPublishOpen(false)} />
              <CancelScheduledProductPublicationPrompt
                event={scheduledPublishEvent}
                isOpen={isCancelPublishPromptOpen}
                closePrompt={handleCancelScheduledPublish}
              />
            </>
          }
        />
        <CreateGlobalItemWizard open={isItemWizardOpen} closeModal={() => setIsItemWizardOpen(false)} />
        <CreateOptionGroupModal open={isOptionGroupModalOpen} onClose={() => setIsOptionGroupModalOpen(false)} />

        {isRangesLoading ? (
          <SpinnerContainer>
            <Spinner size="xlarge" colour="rainbow" />
          </SpinnerContainer>
        ) : (
          <>
            {withIndependentOptionGroups && (
              <TabList>
                <Tab id="products" tabText="Products" />
                <Tab id="option-groups" tabText="Option Groups" />
              </TabList>
            )}
            <TabContent id="products">
              <ProductsTab
                isSyncInProgress={isSyncInProgress}
                onOpenItemWizard={() => setIsItemWizardOpen(true)}
                rangeOptions={rangeOptions}
              />
            </TabContent>
            {withIndependentOptionGroups && (
              <TabContent id="option-groups">
                <OptionGroupsTab
                  isSyncInProgress={isSyncInProgress}
                  onOpenOptionGroupModal={() => setIsOptionGroupModalOpen(true)}
                  rangeOptions={rangeOptions}
                />
              </TabContent>
            )}
          </>
        )}
      </div>
    </TabsProvider>
  );
};
